import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2 as Renderer, ViewChild } from '@angular/core';
import {
  GenericDeviceValidator,
  GetConnextDataQuery,
  SystemEventsService,
  Systems,
  UserPermissions,
  deviceSerialValidator,
} from 'ngx-core-services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';

import {
  POWER_VALIDATOR,
  connextSerialValidator,
  getLoggerState,
  inverterListValidator,
  isConnextLoggerSelected,
  loggerFormValidator,
} from './create.utils';
import { ChooseState } from '../create-block/create-block.model';
import { CreateFirebaseBlockService } from './create-firebase-block.service';
import { defaultConnextDataQuery } from '../block-state/query-constructor.query';
import { labels } from '../create-block/create.model';
import { PositionDetails } from '../components/maps-form/maps-form.models';
import { RemoveInverterFromPlant } from '../virtual-devices-section/virtual-devices-section.component';

import { BaseMonitoringForm } from '../monitoring-section/monitoring-section.component';

@UntilDestroy()
@Component({
  selector: 'create-firebase-block',
  templateUrl: './create-firebase.block.html',
  styleUrls: ['./create-firebase.block.scss'],
})
export class CreateFirebaseBlock implements OnInit {
  @ViewChild('stepper') stepper?: MatStepper;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return { ...this._labels };
  }

  @Input() stepperOrientation: StepperOrientation = 'horizontal';
  @Input() systems$?: Observable<Systems | undefined>;

  @Output() wizardFinished = new EventEmitter<string>();
  @Output() wizardExit = new EventEmitter<void>();
  @Output() onReset = new EventEmitter<void>();

  monitoringForm: BaseMonitoringForm = new FormGroup({
    monitoringPreference: new FormControl<ChooseState[]>([], loggerFormValidator()),
    connextSerial: new FormControl('',
      deviceSerialValidator(['connext']),
      this.genericDeviceValidator.deviceTypeExists(['connext'], { connextFwMinVersion: '1.41.1954' }),
    ),
    importSystemName: new FormControl(''),
  }, {
    validators: connextSerialValidator,
  });

  virtualConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    meterInversion: new FormControl<boolean | null | undefined>(null),
  });

  connextConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    plantPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl('', Validators.required),
  });

  readonly inverterSyncValidators = [
    Validators.required,
    deviceSerialValidator(['inverter', 'meter', 'enerclic']),
  ];

  readonly inverterAsyncValidators = [
    this.genericDeviceValidator.deviceTypeExists(['inverter', 'meter', 'enerclic']),
  ];

  connextDevicesConfig = new FormGroup({
    productionSensor: new FormGroup({
      productionSensorK: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(10),
      ]),
      id: new FormControl('', Validators.required),
      registered: new FormControl(false),
    }),
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', [Validators.required, deviceSerialValidator(['inverter'])]),
        inverterModbusId: new FormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.max(255),
          Validators.pattern('^[0-9]*$'),
        ]),
        setup: new FormControl(false),
        registered: new FormControl(false),
      }),
    ]),
  });

  virtualDevicesConfig = new FormGroup({
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', this.inverterAsyncValidators, this.inverterAsyncValidators),
        added: new FormControl(false, Validators.requiredTrue),
        dismiss: new FormControl<RemoveInverterFromPlant | null>(null),
      }),
    ]),
  }, {
    validators: inverterListValidator,
  });

  connextUsers = new FormGroup({
    users: new FormArray([
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        userLevel: new FormControl(UserPermissions.Admin, Validators.required),
        isSelected: new FormControl(false),
        added: new FormControl(false, Validators.requiredTrue),
      }),
    ]),
    isModified: new FormControl(false),
  });

  isPlantCreated = false;
  connextChangeSubscription?: Subscription;

  readonly loggers: ChooseState[] = [];

  get connextPlantType() {
    return this.connextConfiguration.controls.plantType.value;
  }

  get connextInverterList() {
    return this.connextDevicesConfig.controls.inverterList;
  }

  get is3PhasePlant() {
    return this.connextPlantType === 1;
  }

  get monitoringPreference() {
    return this.monitoringForm.controls.monitoringPreference;
  }

  get connextSerial() {
    return this.monitoringForm.controls.connextSerial;
  }

  get isConnextLoggerSelected() {
    return isConnextLoggerSelected(this.monitoringPreference?.value);
  }

  get isMonitoringFormInvalidOrPending() {
    return this.monitoringForm.invalid || this.monitoringForm.pending;
  }

  get plantConfiguration() {
    return this.isConnextLoggerSelected
      ? this.connextConfiguration
      : this.virtualConfiguration;
  }

  get isPlantConfigurationInvalidOrPending() {
    return this.plantConfiguration.invalid || this.plantConfiguration.pending;
  }

  /**
   * If connext logger is selected, this step is optional
   * If not, it is required, and the user must add at least one inverter
   */
  get isDevicesConfigInvalid() {
    return this.isConnextLoggerSelected
      ? false
      : (this.virtualDevicesConfig.controls.inverterList.length <= 1);
  }

  constructor(
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly getConnextDataQuery: GetConnextDataQuery,
    private readonly systemEventsService: SystemEventsService,
    readonly createFirebaseBlockService: CreateFirebaseBlockService,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
  ) {
    this.loggers = [];
    this.loggers.push(getLoggerState('connext', 1, 'assets/images/svg/connext.svg', this.loggers));
    this.loggers.push(getLoggerState('others', 2, '/assets/images/svg/other-loggers.svg', this.loggers));

    this.createFirebasePlant = this.createFirebasePlant.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.previousStep = this.previousStep.bind(this);
    this.updateConnextConfiguration = this.updateConnextConfiguration.bind(this);
    this.goToConnextPlant = this.goToConnextPlant.bind(this);
    this.onResetAction = this.onResetAction.bind(this);
    this.goToHome = this.goToHome.bind(this);
  }

  goToHome() {
    this.wizardExit.emit();
  }

  onResetAction() {
    this.reset();
    this.onReset.emit();
  }

  nextStep() {
    this.stepper?.next();
  }

  previousStep() {
    this.stepper?.previous();
  }

  ngOnInit() {
    this.systems$?.pipe(
      untilDestroyed(this),
    ).subscribe((systems) => {
      if (systems?.length) {
        this.connextSerial.clearValidators();
        this.connextSerial.setValidators([deviceSerialValidator(['connext'])]);
        this.connextSerial.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems,
            (value) => this.genericDeviceValidator.getConnextUid(value).then((r) => r || value),
            'configuration.connextId',
          ),
          this.genericDeviceValidator.deviceTypeExists(['connext'], { connextFwMinVersion: '1.41.1954' }),
        ]);

        const plantUid = this.connextConfiguration.controls.plantUid.value;
        if (plantUid) {
          systems = systems.filter((system) => system.uid !== plantUid);
        }

        this.virtualConfiguration.controls.plantName.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems,
            async (value) => value,
            'name',
          ),
        ]);

        this.connextConfiguration.controls.plantName.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems,
            async (value) => value,
            'name',
          ),
        ]);
      }
    });

    this.connextSerial.statusChanges.pipe(
      untilDestroyed(this),
    ).subscribe(this.updateConnextConfiguration);
  }

  async updateConnextConfiguration(status: string | null) {
    if (!status || status !== 'VALID') {
      this.connextChangeSubscription?.unsubscribe();
      return;
    }

    const serial = this.connextSerial.value;
    if (!serial) {
      return;
    }

    const uid = await this.genericDeviceValidator.getConnextUid(serial);
    if (!uid) {
      return;
    }

    this.connextChangeSubscription = this.systemEventsService.onUnknownSystemEvent(uid).subscribe(async (_) => {
      const useOnlyNetwork = true;
      const connextData = await this.getConnextDataQuery.execute(defaultConnextDataQuery, { id: uid }, useOnlyNetwork);
      try {
        const users = connextData.data.getConnextData.users;
        if (users?.length) {
          this.connextUsers.controls.users.clear();
          users.forEach((user) => {
            this.connextUsers.controls.users.push(new FormGroup({
              email: new FormControl(user.email, [Validators.required, Validators.email]),
              userLevel: new FormControl(user.userLevel, Validators.required),
              isSelected: new FormControl(false),
              added: new FormControl(true),
            }));
          });

          this.connextUsers.controls.users.push(new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            userLevel: new FormControl<number>(UserPermissions.Admin, Validators.required),
            isSelected: new FormControl(false),
            added: new FormControl(false),
          }));

          this.connextUsers.controls.isModified.setValue(false);
        }
      } catch {}
    });
  }

  async createFirebasePlant() {
    const virtualDevices = this.virtualDevicesConfig.controls.inverterList.value
      .filter((inverter: any) => inverter.added)
      .map((inverter: any) => inverter.inverterSerial)
      /** Filter duplicates */
      .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);

    const name = this.plantConfiguration.controls.plantName.value as string;
    const coordinates = this.plantConfiguration.controls.coordinates.value as PositionDetails;
    const response = await this.createFirebaseBlockService.createFirebasePlant(name, {
      virtualDevices,
      coordinates,
    })();

    if (response.success?.uid) {
      this.wizardFinished.emit(response.success?.uid);
    }
  }

  goToConnextPlant() {
    const plantUid = this.connextConfiguration.controls.plantUid.value;
    if (!plantUid) {
      return;
    }

    this.wizardFinished.emit(plantUid);
  }

  reset() {
    this.monitoringForm.controls.connextSerial.setValue('');
    this.monitoringForm.controls.importSystemName.setValue('');
    this.monitoringForm.markAsUntouched();

    this.virtualConfiguration.controls.plantName.setValue('');
    this.virtualConfiguration.controls.coordinates.setValue(null);
    this.virtualDevicesConfig.markAsUntouched();

    this.connextConfiguration.controls.plantName.setValue('');
    this.connextConfiguration.controls.coordinates.setValue(null);
    this.connextConfiguration.controls.plantPower.setValue(null);
    this.connextConfiguration.controls.plantType.setValue(null);
    this.connextConfiguration.controls.applied.setValue(false);
    this.connextConfiguration.controls.plantUid.setValue('');
    this.connextConfiguration.markAsUntouched();

    this.connextDevicesConfig.controls.productionSensor.setValue({
      productionSensorK: null,
      id: '',
      registered: false,
    });
    this.connextDevicesConfig.controls.inverterList.clear();
    this.connextDevicesConfig.controls.inverterList.push(new FormGroup({
      inverterSerial: new FormControl('', [Validators.required, deviceSerialValidator(['inverter'])]),
      inverterModbusId: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(255),
        Validators.pattern('^[0-9]*$'),
      ]),
      setup: new FormControl(false),
      registered: new FormControl(false),
    }));
    this.connextDevicesConfig.markAsUntouched();

    this.virtualDevicesConfig.controls.inverterList.clear();
    this.virtualDevicesConfig.controls.inverterList.push(new FormGroup({
      inverterSerial: new FormControl('', [
        Validators.required,
        deviceSerialValidator(['inverter', 'meter', 'enerclic']),
      ], [
        this.genericDeviceValidator.deviceTypeExists(['inverter', 'meter', 'enerclic']),
      ]),
      added: new FormControl(false),
      dismiss: new FormControl<RemoveInverterFromPlant | null>(null),
    }));
    this.virtualDevicesConfig.markAsUntouched();
  }
}
